import moment from "moment";
import { Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { DATE_FORMATS } from "../../../../../constants";
import { CopyComponent } from "../../../../../parts/CopyComponent";
import { PasteExerciseComponent } from "../../../../../parts/PasteExerciseComponent";
import SvgIcon from "../../../../../parts/SvgIcon";
import ToolTip from "../../../../../parts/tooltip-portal";
import {
  CALENDAR_LIST,
  registerDropList,
  syncDraggables,
} from "../../../../../utils/collapse-drag-controller";
import { groupExercisesByComplex } from "../../../../../utils/complex";
import {
  buildSupersets,
  computeSectionPrefixes,
  exerciseIdsOfRows,
  getHighestOrder,
  groupExercisesBySection,
  groupOptimisticMove,
  groupRowsFromElements,
  isSameGroup,
  moveSupersetAcrossLists,
  orderAmongSections,
  planSectionGroupDrop,
  reorderPreservingSupersets,
  rowsFromDraggables,
  sectionOrdersForIds,
  sectionOrdersFromRows,
  supersetChains,
} from "../../../../../utils/prefix-calculator";
import { splitSections } from "../../../../../utils/section-types";
import { useMenstrualCycleDayInfo } from "../../menstrual-cycle/useMenstrualCycleDayInfo";
import ExerciseEditor from "./ExerciseEditor/ExerciseEditor";
import Footer from "./ExerciseEditor/Footer";
import SectionSeparator from "./ExerciseEditor/SectionSeparator";
import WorkoutNoteBlock from "./ExerciseEditor/WorkoutNoteBlock";

const { DATE_DAY_FORMAT } = DATE_FORMATS;

const DayEditor = ({
  date,
  editDayMode,
  toggleEditDayMode,
  showAddExercise,
  exercises,
  resetExercises,
  handleRemoveMultiple,
  saveEditedExercise,
  setState,
  dayString,
  athleteId,
  tempExercises,
  supersets,
  handleSelectChange,
  selectedExercise,
  showOptions,
  activeUser,
  athlete,
  getTempExercise,
  handleInput,
  addTempSet,
  removeTempSet,
  repMaximums,
  removeSet,
  handleSuperset,
  withWeight,
  fromMetric,
  intensityMeasure,
  isRiR,
  opacity,
  activeUserToRequest,
  scheduledSessions,
  removeTempExercise,
  query,
  femaleCycleLogs,
  femaleCycleSettings,
  sections = [],
  editSection,
}) => {
  const { t } = useTranslation("athlete-portal");
  // The drop handler reads props through this ref so the list registration
  // never has to be rebuilt when they change.
  const latestRef = useRef();
  latestRef.current = {
    formattedDate: date.format(DATE_DAY_FORMAT),
    scheduledSessions,
    sections,
    t,
    athleteId,
    removeTempExercise,
  };
  const draggablesRef = useRef(new Map());
  const handleDropRef = useRef(null);
  handleDropRef.current = (e) => {
    const {
      formattedDate,
      scheduledSessions,
      sections,
      t,
      athleteId,
      removeTempExercise,
    } = latestRef.current;
    const { item, from, to, oldIndex, newIndex } = e;
    const sameList = from === to;

    // Section group (header + every row under it): the controller leaves the
    // DOM alone, so the new layout is computed from the rows as data.
    if (e.group) {
      const dayRows = (list) =>
        groupRowsFromElements(
          list.querySelectorAll(
            ":scope > .exercisex, :scope > .section-separator"
          ),
          scheduledSessions[list.id] || []
        );
      const { fromRows, toRows, optimistic } = planSectionGroupDrop(
        item,
        dayRows(from),
        newIndex,
        sameList ? null : dayRows(to)
      );

      const fromIds = exerciseIdsOfRows(fromRows);
      let body = {
        exercises: fromIds,
        sections: sectionOrdersFromRows(fromRows),
      };
      if (sameList)
        body.supersets = supersetChains(
          scheduledSessions[formattedDate] || [],
          fromIds
        );
      else
        body = {
          ...body,
          toExercises: exerciseIdsOfRows(toRows),
          toDate: moment(to.id, DATE_DAY_FORMAT).startOf("day").toJSON(),
          toSections: sectionOrdersFromRows(toRows),
        };

      submit(
        `/actions/exercise/workout-reorder?proxyAthlete=${athleteId}&reqSource=calendar`,
        { method: "POST", body }
      );
      return optimistic;
    }

    if (oldIndex === newIndex && sameList) return;

    // Reposition a section separator. Dropped on top of another section,
    // swap the two sections' orders; otherwise compute a fractional order
    // between its new exercise neighbors. Exercises are untouched either way.
    if (item.classList.contains("section-separator")) {
      const fromDraggable = from.querySelectorAll(
        ":scope > .exercisex, :scope > .section-separator"
      );
      if (!sameList) {
        // Moving a section to a different day: reposition it between its
        // new exercise neighbors there and repoint its date, mirroring
        // the same-list midpoint logic below (minus the adjacent-swap
        // case, which has no cross-day equivalent).
        const revertCrossList = () =>
          from.insertBefore(item, fromDraggable[oldIndex]);

        const toDayExercises = scheduledSessions[to.id] || [];
        const orderOfTo = (el) =>
          el ? toDayExercises.find((ex) => ex._id === el.id)?.order : null;

        let toPrevEl = item.previousElementSibling;
        while (toPrevEl && !toPrevEl.classList.contains("exercisex"))
          toPrevEl = toPrevEl.previousElementSibling;
        let toNextEl = item.nextElementSibling;
        while (toNextEl && !toNextEl.classList.contains("exercisex"))
          toNextEl = toNextEl.nextElementSibling;

        const toPrevExercise = toPrevEl
          ? toDayExercises.find((ex) => ex._id === toPrevEl.id)
          : null;
        const toNextExercise = toNextEl
          ? toDayExercises.find((ex) => ex._id === toNextEl.id)
          : null;

        if (isSameGroup(toPrevExercise, toNextExercise)) {
          revertCrossList();
          submit("/actions/common/toast", {
            method: "POST",
            body: {
              type: "error",
              message: t("dayEditor.cannotDropSectionInGroup"),
            },
          });
          return;
        }

        const toPrevOrder = orderOfTo(toPrevEl);
        const toNextOrder = orderOfTo(toNextEl);

        let crossListOrder;
        if (toPrevOrder != null && toNextOrder != null)
          crossListOrder = (toPrevOrder + toNextOrder) / 2;
        else if (toPrevOrder != null) crossListOrder = toPrevOrder + 0.5;
        else if (toNextOrder != null) crossListOrder = toNextOrder - 0.5;
        // Target day has no exercises (empty, or only sections).
        else crossListOrder = orderAmongSections(item);

        revertCrossList();

        const toMoment = moment(to.id, DATE_DAY_FORMAT).startOf("day");
        submit(
          `/actions/exercise-section/update/${item.getAttribute(
            "data-section-id"
          )}?proxyAthlete=${athleteId}&reqSource=calendar`,
          {
            method: "POST",
            body: {
              order: crossListOrder,
              date: toMoment.toJSON(),
              utcDate: Number(toMoment.format("YYYYMMDD")),
            },
          }
        );
        return true;
      }

      // Revert the optimistic DOM move so React reconciliation stays sane;
      // the persisted order drives the real position after refresh.
      const revert = () =>
        from.insertBefore(
          item,
          fromDraggable[oldIndex + (oldIndex > newIndex)]
        );

      // Dropped directly on top of another section (no exercise between
      // the two separators)? A plain midpoint would tie their orders and
      // leave one section owning zero exercises (banished to the bottom).
      // Instead, swap the two sections' orders so the headers exchange
      // places while the exercises under each stay put.
      const adjacentSeparator =
        (item.previousElementSibling?.classList.contains("section-separator") &&
          item.previousElementSibling) ||
        (item.nextElementSibling?.classList.contains("section-separator") &&
          item.nextElementSibling) ||
        null;

      if (adjacentSeparator) {
        const draggedId = item.getAttribute("data-section-id");
        const targetId = adjacentSeparator.getAttribute("data-section-id");
        const orderOfSection = (id) =>
          sections.find((s) => s._id === id)?.order;
        const draggedOrder = orderOfSection(draggedId);
        const targetOrder = orderOfSection(targetId);

        revert();

        if (
          draggedId &&
          targetId &&
          draggedOrder != null &&
          targetOrder != null
        ) {
          // Swap via the batch reorder action: a single full-page submit
          // can't carry two separate section updates. Empty `exercises`
          // leaves exercise orders untouched.
          submit(
            `/actions/exercise/workout-reorder?proxyAthlete=${athleteId}&reqSource=calendar`,
            {
              method: "POST",
              body: {
                exercises: [],
                sections: [
                  { _id: draggedId, order: targetOrder },
                  { _id: targetId, order: draggedOrder },
                ],
              },
            }
          );
        }
        return;
      }

      const dayExercises = scheduledSessions[formattedDate] || [];
      const orderOf = (el) =>
        el ? dayExercises.find((ex) => ex._id === el.id)?.order : null;

      let prevEl = item.previousElementSibling;
      while (prevEl && !prevEl.classList.contains("exercisex"))
        prevEl = prevEl.previousElementSibling;
      let nextEl = item.nextElementSibling;
      while (nextEl && !nextEl.classList.contains("exercisex"))
        nextEl = nextEl.nextElementSibling;

      const prevExercise = prevEl
        ? dayExercises.find((ex) => ex._id === prevEl.id)
        : null;
      const nextExercise = nextEl
        ? dayExercises.find((ex) => ex._id === nextEl.id)
        : null;

      if (isSameGroup(prevExercise, nextExercise)) {
        revert();
        submit("/actions/common/toast", {
          method: "POST",
          body: {
            type: "error",
            message: t("dayEditor.cannotDropSectionInGroup"),
          },
        });
        return;
      }

      const prevOrder = orderOf(prevEl);
      const nextOrder = orderOf(nextEl);

      let newOrder = null;
      if (prevOrder != null && nextOrder != null)
        newOrder = (prevOrder + nextOrder) / 2;
      else if (prevOrder != null) newOrder = prevOrder + 0.5;
      else if (nextOrder != null) newOrder = nextOrder - 0.5;

      revert();

      if (newOrder != null) {
        submit(
          `/actions/exercise-section/update/${item.getAttribute(
            "data-section-id"
          )}?proxyAthlete=${athleteId}&reqSource=calendar`,
          { method: "POST", body: { order: newOrder } }
        );
      }
      return newOrder != null;
    }

    // Revert list matches the draggable selector so oldIndex/newIndex
    // (which count separators too) map correctly back onto the DOM.
    const fromDraggable = from.querySelectorAll(
      ":scope > .exercisex, :scope > .section-separator"
    );
    const fromItems = from.querySelectorAll(":scope > .exercisex");
    const toItems = to.querySelectorAll(":scope > .exercisex");
    // Combined (exercises + separators) lists in their dropped order,
    // captured before the revert below so section orders can be
    // recomputed relative to the new layout (static NodeList survives it).
    const toDraggable = sameList
      ? fromDraggable
      : to.querySelectorAll(":scope > .exercisex, :scope > .section-separator");
    const toDate = to.id;
    const fromExercises = [];
    const toExercises = [];
    let exercises = scheduledSessions[formattedDate] || [];
    if (!sameList) {
      exercises = [...exercises, ...(scheduledSessions[toDate] || [])];
    }

    for (let i = 0; i < fromItems.length; i++) {
      const element = fromItems[i];
      const exercise = exercises.find((exercise) => {
        return exercise._id === element.id;
      });

      if (exercise?.complex && exercise?.complexInstance) {
        const complexExercises = exercises
          .filter(
            (e) =>
              e.complex === exercise.complex &&
              e.complexInstance === exercise.complexInstance
          )
          .sort((ex1, ex2) => ex1.indexInComplex - ex2.indexInComplex)
          .map((ex) => ex._id);
        fromExercises.push(...complexExercises);
      } else {
        fromExercises.push(element.id);
      }
    }
    if (!sameList) {
      for (let i = 0; i < toItems.length; i++) {
        const element = toItems[i];
        const exercise = exercises.find((exercise) => {
          return exercise._id === element.id;
        });

        if (exercise?.complex && exercise?.complexInstance) {
          const complexExercises = exercises
            .filter(
              (e) =>
                e.complex === exercise.complex &&
                e.complexInstance === exercise.complexInstance
            )
            .sort((ex1, ex2) => ex1.indexInComplex - ex2.indexInComplex)
            .map((ex) => ex._id);
          toExercises.push(...complexExercises);
        } else {
          toExercises.push(element.id);
        }
      }
      from.insertBefore(item, fromDraggable[oldIndex]);
    } else {
      from.insertBefore(item, fromDraggable[oldIndex + (oldIndex > newIndex)]);
    }

    // Keep supersets/complexes atomic: the drag moves one row, so move the
    // whole group together and never split another group on drop-in.
    // See reorderPreservingSupersets in prefix-calculator.
    let orderedFrom;
    let orderedTo;
    let movedGroupIds = [];
    if (sameList) {
      orderedFrom = reorderPreservingSupersets(
        scheduledSessions[formattedDate] || [],
        fromExercises,
        item.id
      );
      movedGroupIds =
        buildSupersets(scheduledSessions[formattedDate] || [])
          .find((group) => group.ids.map(String).includes(String(item.id)))
          ?.ids.map(String) || [];
    } else {
      // Cross-day: carry the dragged row's whole group to the destination,
      // kept grouped, and remove it from the source.
      const { fromIds, toIds, groupIds } = moveSupersetAcrossLists(
        scheduledSessions[formattedDate] || [],
        scheduledSessions[toDate] || [],
        item.id,
        fromExercises,
        toExercises
      );
      orderedFrom = fromIds;
      orderedTo = toIds;
      movedGroupIds = groupIds;
    }

    // The DOM has one row per complex and, cross-day, only the grabbed row of a
    // superset; the order lists hold every exercise, so section orders follow them.
    let body = {
      exercises: orderedFrom,
      sections: sectionOrdersForIds(
        rowsFromDraggables(fromDraggable),
        orderedFrom,
        sameList ? movedGroupIds : [],
        item.id
      ),
    };
    if (sameList) {
      body.supersets = supersetChains(
        scheduledSessions[formattedDate] || [],
        orderedFrom
      );
    }

    if (!sameList && toExercises.length > 0) {
      body = {
        ...body,
        toExercises: orderedTo,
        toDate: moment(toDate, DATE_DAY_FORMAT).startOf("day").toJSON(),
        toSections: sectionOrdersForIds(
          rowsFromDraggables(toDraggable),
          orderedTo,
          movedGroupIds
        ),
      };
    }

    removeTempExercise(item.id, sameList, orderedFrom);
    submit(
      `/actions/exercise/workout-reorder?proxyAthlete=${athleteId}&reqSource=calendar`,
      {
        method: "POST",
        body,
      }
    );

    // Show the dragged group (a lone row, superset or complex) where the save
    // will put it, which is not always the slot under the pointer.
    return groupOptimisticMove(
      scheduledSessions[formattedDate] || [],
      item.id,
      sameList ? orderedFrom : orderedTo
    );
  };
  useEffect(() => {
    const list = document.getElementById(latestRef.current.formattedDate);
    if (!list) return;
    return registerDropList(
      list,
      (event) => handleDropRef.current(event),
      CALENDAR_LIST,
      () =>
        buildSupersets(
          latestRef.current.scheduledSessions[
            latestRef.current.formattedDate
          ] || []
        )
    );
  }, []);
  useEffect(() => {
    const list = document.getElementById(latestRef.current.formattedDate);
    if (list) syncDraggables(list, draggablesRef.current);
  });
  useEffect(
    () => () => {
      for (const entry of draggablesRef.current.values()) entry.destroy();
      draggablesRef.current.clear();
    },
    []
  );

  const { menstrualCycleInfo, predictedCycleInfo } = useMenstrualCycleDayInfo(
    date,
    femaleCycleLogs,
    femaleCycleSettings
  );

  const handleClick = (evt) => {
    if (evt.target.alt === "dismiss") {
      evt.preventDefault();
      return;
    }
    if (
      evt.target.href &&
      evt.target.href.indexOf("calendar-add-exercise") > 0
    ) {
      return;
    }
    if (exercises.length) {
      // Avoiding activate the edit mode if click on the options meatball.
      toggleEditDayMode();
    }
  };

  const groupedExercises = groupExercisesByComplex(exercises);

  // The workout note is rendered separately, above the exercises container
  // (see the <WorkoutNoteBlock> below) — pull it out of `sections` so it
  // never enters the separator-grouping/ordering logic. `sections` (unsplit)
  // is still used for the drag handlers below and for CopyComponent, which
  // intentionally carries the note along when copying the day.
  const { sections: normalSections, note } = splitSections(sections);

  // Section overlay: per-section letter reset + where each separator renders.
  const sectionPrefixes = computeSectionPrefixes(exercises, normalSections);
  const sectionGroups = groupExercisesBySection(exercises, normalSections);
  const sectionByFirstExerciseId = {};
  const emptySections = [];
  sectionGroups.forEach((group) => {
    if (!group.section) return;
    if (group.exercises.length) {
      sectionByFirstExerciseId[group.exercises[0]._id] = group.section;
    } else {
      emptySections.push(group.section);
    }
  });
  // Empty sections must keep their ordered position, not get dumped at the end.
  // Each renders just before the first exercise whose order >= its own (that row
  // always leads a section); sections after the last exercise stay trailing.
  const emptySectionsBeforeId = {};
  const trailingEmptySections = [];
  if (emptySections.length) {
    const sortedRows = [...groupedExercises].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    emptySections.forEach((section) => {
      const before = sortedRows.find(
        (row) => (row.order ?? 0) >= (section.order ?? 0)
      );
      if (before) {
        if (!emptySectionsBeforeId[before._id])
          emptySectionsBeforeId[before._id] = [];
        emptySectionsBeforeId[before._id].push(section);
      } else {
        trailingEmptySections.push(section);
      }
    });
  }

  return (
    <div className="day-editor" style={{ opacity: opacity ? 0.3 : 1 }}>
      <header>
        <p className="s1">{date.format("D")}</p>
        <div className="flex items-center gap-2">
          {(menstrualCycleInfo || predictedCycleInfo) && (
            <ToolTip
              body={
                predictedCycleInfo
                  ? t("dayEditor.predictedCycle")
                  : t("dayEditor.menstrualCycle")
              }
            >
              <a
                href={`/athlete-portal/metrics/female-cycle?date=${date.format("YYYYMMDD")}&athlete=${athleteId}${query?.organizationCoach ? `&organizationCoach=${query.organizationCoach}` : ""}`}
                onClick={(e) => e.stopPropagation()}
              >
                <SvgIcon
                  src="/img/new-icons/blood-drop-icon.svg"
                  noFilterOnLight
                />
              </a>
            </ToolTip>
          )}
          {exercises && exercises.length > 0 && (
            <CopyComponent
              classNames="p-0! bg-(--secondary-background-color) h-7 w-7"
              exercises={exercises}
              athlete={athlete}
              sections={sections}
            />
          )}
          {exercises && exercises.length === 0 && (
            <PasteExerciseComponent
              athlete={athlete}
              date={date.format(DATE_DAY_FORMAT)}
              classNames="p-0 bg-(--secondary-background-color) h-7 w-7"
              withFullInfo={false}
              isProgram={false}
              query={query?.exercisesInClipboard}
            />
          )}
        </div>
      </header>
      <section className={editDayMode ? "height-tight" : ""}>
        <WorkoutNoteBlock
          key={`${date.format(DATE_DAY_FORMAT)}-${note?._id ?? "new"}`}
          note={note}
          athleteId={athleteId}
          reqSource="calendar"
        />
        <div
          className={`exercises${editDayMode ? " editing" : ""}`}
          id={date.format(DATE_DAY_FORMAT)}
          onClick={handleClick}
        >
          {groupedExercises?.length || emptySections.length ? (
            <>
              {groupedExercises.map((exercise, idx) => (
                <Fragment key={exercise._id}>
                  {(emptySectionsBeforeId[exercise._id] || []).map(
                    (section) => (
                      <SectionSeparator
                        key={section._id}
                        section={section}
                        athleteId={athleteId}
                        dragHandleClass="grabber cursor-grab"
                        groupDrag
                        autoEdit={section._id === editSection}
                      />
                    )
                  )}
                  {sectionByFirstExerciseId[exercise._id] && (
                    <SectionSeparator
                      // Key on the section id (not position) so swapping which
                      // section leads an exercise remounts the separator with
                      // fresh local state instead of reusing the old section's
                      // (e.g. a stale name in the rename input).
                      key={sectionByFirstExerciseId[exercise._id]._id}
                      section={sectionByFirstExerciseId[exercise._id]}
                      athleteId={athleteId}
                      dragHandleClass="grabber cursor-grab"
                      groupDrag
                      autoEdit={
                        sectionByFirstExerciseId[exercise._id]._id ===
                        editSection
                      }
                    />
                  )}
                  <ExerciseEditor
                    keyId={idx}
                    {...{
                      exercise,
                      exercises,
                      activeUser,
                      athlete,
                      editDayMode,
                      dayString,
                      athleteId,
                      tempExercises,
                      supersets,
                      sectionPrefix: sectionPrefixes[exercise._id],
                      handleSelectChange,
                      selectedExercise,
                      showOptions,
                      getTempExercise,
                      handleInput,
                      addTempSet,
                      removeTempSet,
                      repMaximums,
                      removeSet,
                      handleSuperset,
                      withWeight,
                      fromMetric,
                      intensityMeasure,
                      isRiR,
                      activeUserToRequest,
                      date,
                    }}
                  />
                </Fragment>
              ))}
              {trailingEmptySections.map((section) => (
                <SectionSeparator
                  key={section._id}
                  section={section}
                  athleteId={athleteId}
                  dragHandleClass="grabber cursor-grab"
                  groupDrag
                  autoEdit={section._id === editSection}
                />
              ))}
            </>
          ) : (
            <div
              className="no-exercises-container"
              onMouseEnter={() => setState({ showAddExercise: true })}
              onMouseLeave={() => setState({ showAddExercise: false })}
            >
              <div
                className="add-paste-exercise"
                style={showAddExercise ? { opacity: 1 } : { opacity: 0 }}
              >
                <a
                  className="h-27"
                  href={`/actions/refresh?modal[type]=calendar-add-exercise&day=${date.format(
                    DATE_DAY_FORMAT
                  )}`}
                  aria-disabled={!showAddExercise}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-solid border-(--white-20)">
                    <SvgIcon
                      src="/img/new-icons/add.svg"
                      className="h-11 w-11"
                    />
                  </span>
                  <p className="p4 text-center">{t("dayEditor.addExercise")}</p>
                </a>
                <PasteExerciseComponent
                  athlete={athlete}
                  classNames="h-27"
                  date={date.format(DATE_DAY_FORMAT)}
                  withFullInfo={true}
                  showAddExercise={showAddExercise}
                  isProgram={false}
                  query={query?.exercisesInClipboard}
                />
                <a
                  className="h-27"
                  href={`/actions/refresh?modal[type]=import-workout&day=${date.format(
                    DATE_DAY_FORMAT
                  )}&exercisesLength=0&highestOrder=0`}
                  aria-disabled={!showAddExercise}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-solid border-(--white-20)">
                    <SvgIcon
                      src="/img/new-icons/import.svg"
                      className="h-6 w-6"
                    />
                  </span>
                  <p className="p4 text-center">
                    {t("dayEditor.importWorkout")}
                  </p>
                </a>
                <a
                  className="h-27"
                  href="#_"
                  aria-disabled={!showAddExercise}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!showAddExercise) return;
                    submit(
                      `/actions/exercise-section/create?proxyAthlete=${athleteId}&reqSource=calendar`,
                      {
                        method: "POST",
                        body: {
                          name: t("exerciseEditor.newSection"),
                          // Sit just below any existing content so exercises
                          // added afterwards (highestOrder + 1) fall under it.
                          order:
                            getHighestOrder(exercises, normalSections) + 0.5,
                          utcDate: Number(date.format("YYYYMMDD")),
                          date: date.toJSON(),
                        },
                      }
                    );
                  }}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-solid border-(--white-20)">
                    <SvgIcon
                      src="/img/new-icons/workout-sections-icon.svg"
                      classNames="h-6 w-6"
                    />
                  </span>
                  <p className="p4 text-center">
                    {t("dayEditor.addWorkoutSection")}
                  </p>
                </a>
              </div>
            </div>
          )}
        </div>
        <Footer
          date={date}
          editDayMode={editDayMode}
          handleRemoveMultiple={handleRemoveMultiple}
          resetExercises={resetExercises}
          saveEditedExercise={saveEditedExercise}
          toggleEditDayMode={toggleEditDayMode}
          hasExercise={exercises?.length}
          athleteId={athleteId}
          exercisesInClipboard={query?.exercisesInClipboard}
          athlete={athlete}
          exercises={exercises}
          sections={normalSections}
        />
      </section>
    </div>
  );
};

export default DayEditor;
